/**
 * Version Checker Utility
 *
 * This utility checks if the current running version of the app matches
 * the deployed version. If there's a mismatch, it means the user has
 * cached code and should refresh to get the latest version.
 *
 * It also validates the user's session every check interval to ensure
 * they're still logged in. If the session is invalid, they'll be
 * redirected to the login page automatically.
 */

import { userService } from '../services';
import { getLocalStorage } from '../shared/storage';
import packageJson from '../../package.json';

// Get the current version from package.json (this gets bundled with the app)
const CURRENT_VERSION = packageJson.version;

// Check interval (default: 5 minutes)
const DEFAULT_CHECK_INTERVAL = 5 * 60 * 1000;

// Detect if this is a test/RC build (version contains 'vRC' or commit hash)
// Can be overridden with localStorage flag for testing
const isTestBuild = () => {
  // Check if version checking is force-enabled for testing
  const forceEnable = localStorage.getItem('enableVersionCheck');
  const hasTestVersion = CURRENT_VERSION.includes('vRC') || CURRENT_VERSION.includes('RC');

  if (forceEnable === 'true') {
    return false; // Treat as production build - version checking ENABLED
  }

  return hasTestVersion; // If true, version checking DISABLED
};

class VersionChecker {
  constructor() {
    this.checkInterval = null;
    this.onVersionMismatch = null;
    this.currentVersion = CURRENT_VERSION;
    this.isChecking = false;
  }

  /**
   * Start checking for version updates
   * @param {Function} callback - Called when version mismatch is detected
   * @param {Number} interval - Check interval in milliseconds
   */
  start(callback, interval = DEFAULT_CHECK_INTERVAL) {
    this.onVersionMismatch = callback;

    // Perform initial check after a short delay
    setTimeout(() => this.checkVersion(), 10000); // Check 10 seconds after app loads

    // Set up periodic checks
    this.checkInterval = setInterval(() => {
      this.checkVersion();
    }, interval);
  }

  /**
   * Stop checking for version updates
   */
  stop() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }

  /**
   * Validate user session
   * Checks if the user is still logged in by calling refreshLogin
   * If session is invalid, user will be automatically logged out
   */
  async validateSession() {
    try {
      const userJson = getLocalStorage('user');
      if (!userJson) {
        // No user logged in, skip session check
        return true;
      }

      const user = JSON.parse(userJson);
      if (!user || !user.jwtToken) {
        // Invalid user data, skip
        return true;
      }

      // Call refreshLogin to validate session
      // If token is invalid (401), userService will automatically logout and redirect
      await userService.refreshLogin(user);
      return true;
    } catch (error) {
      // Error will be handled by userService (logout + redirect if 401)
      console.warn('Session validation failed:', error);
      return false;
    }
  }

  /**
   * Check if a new version is available and validate user session
   * @returns {Promise<boolean>} True if version mismatch detected
   */
  async checkVersion() {
    // Prevent multiple simultaneous checks
    if (this.isChecking) {
      return false;
    }

    this.isChecking = true;

    try {
      // First, validate the user's session (if logged in)
      // This happens silently in the background
      // TEMPORARILY DISABLED - Uncomment the line below to re-enable session validation
      // await this.validateSession();

      // Skip version checking for test/RC builds since they don't update version.json
      if (isTestBuild()) {
        return false;
      }

      // Then check version
      // Add timestamp to prevent caching of version.json
      const timestamp = new Date().getTime();
      const response = await fetch(`/version.json?t=${timestamp}`, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      });

      if (!response.ok) {
        console.warn('Failed to fetch version info');
        return false;
      }

      const data = await response.json();
      const serverVersion = data.version;

      // Compare versions
      if (serverVersion !== this.currentVersion) {
        console.log(`Version mismatch detected. Current: ${this.currentVersion}, Server: ${serverVersion}`);

        // Stop checking once mismatch is detected
        this.stop();

        // Notify callback
        if (this.onVersionMismatch) {
          this.onVersionMismatch(serverVersion, this.currentVersion);
        }

        return true;
      }

      return false;
    } catch (error) {
      console.error('Error checking version:', error);
      return false;
    } finally {
      this.isChecking = false;
    }
  }

  /**
   * Force a page reload to get the latest version
   * Uses multiple strategies to ensure cache is cleared
   */
  reloadApp() {
    // Strategy 1: Clear localStorage/sessionStorage (except critical items)
    const criticalKeys = ['user', 'sellerSelectOption', 'devDomain'];
    const savedData = {};
    criticalKeys.forEach((key) => {
      const value = localStorage.getItem(key);
      if (value) {
        savedData[key] = value;
      }
    });

    // Clear all storage
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (e) {
      console.warn('Could not clear storage:', e);
    }

    // Restore critical data
    Object.keys(savedData).forEach((key) => {
      localStorage.setItem(key, savedData[key]);
    });

    // Strategy 2: Clear service worker cache
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((registration) => {
          registration.unregister();
        });
      });
    }

    // Strategy 3: Clear all caches
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => {
          caches.delete(name);
        });
      });
    }

    // Strategy 4: Force reload with cache bypass
    // Add timestamp to URL to force complete reload
    const url = new URL(window.location.href);
    url.searchParams.set('_reload', Date.now());
    window.location.href = url.href;
  }
}

// Export singleton instance
export default new VersionChecker();
